import { Divider } from '@byecode/ui'
import type { ChartType } from '@lighthouse/core'
import { useAtomValue } from 'jotai'
import React, { useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { AnchorSetting } from '@/components/AnchorSetting'
import { EventList } from '@/components/EventList'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'

import { dataSourceAtom } from '../atoms'
import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'
import { ChartTypeSwitcher } from './ChartTypeSwitcher'
import { Data } from './Data'
import { DefaultDataSource } from './DataSource'
import { Design } from './Design'
import type { ChartFormSlice } from './formSlice'
import { IndicatorCardConfigurator } from './IndicatorCardConfigurator'

const SCxBox = styled.div`
    padding: 12px 16px 0 16px;
`

const SCxContainer = styled.div`
    height: 100%;
`

const SCxHeader = styled.div`
    font-weight: 600;
    padding: 6px 0;
`

interface ChartBlockSettingsProps {}

export const ChartBlockSettings: React.FC<ChartBlockSettingsProps> = () => {
    const dataSource = useAtomValue(dataSourceAtom)
    const { control } = useFormContext<ChartFormSlice>()
    const chartType = (useWatch({ control, name: 'chartType' }) ?? 'bar') as ChartType
    const styleProps = getBlockAndPageDesignLimit('chart')
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)

    const setting = useMemo(() => {
        if (chartType === 'indicator') {
            return <IndicatorCardConfigurator />
        }

        return (
            <>
                <DefaultDataSource />
                {dataSource && <Data dataSource={dataSource} />}
                <Design />
            </>
        )
    }, [chartType, dataSource])

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />
            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <SCxBox>
                        <SCxHeader>类型</SCxHeader>
                        <Controller
                            control={control}
                            name="chartType"
                            render={({ field }) => {
                                return <ChartTypeSwitcher value={(field.value ?? 'bar') as ChartType} onChange={field.onChange} />
                            }}
                        />
                    </SCxBox>
                    <Divider />
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <Controller control={control} name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    {setting}
                    <Divider />
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            <SCxContainer key={BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION}>
                <Controller
                    name="eventList"
                    control={control}
                    render={({ field }) => <EventList blockType="chart" value={field.value ?? []} onChange={field.onChange} />}
                />
                <Divider />
                <AnimationSetting />
            </SCxContainer>
        </>
    )
}
